import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { RecoveryCodesList } from '@/components/common/RecoveryCodesList';
import { useRegenerateRecoveryCodes } from '@/hooks/useRegenerateRecoveryCodes';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import {
  ACCOUNT_SECURITY_STRINGS as S,
  TWO_FACTOR_SETUP_STRINGS,
} from '@/lib/constants/two-factor-strings';
import { getApiErrorMessage, mapApiError } from '@/lib/utils/error.utils';
import { RescanAuthenticatorDialog } from './RescanAuthenticatorDialog';

const ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.TOTP_NOT_ENROLLED]: S.NOT_ENROLLED,
  [API_ERROR_CODES.TWO_FACTOR_LOCKED]: TWO_FACTOR_SETUP_STRINGS.ERROR_LOCKED,
};

/**
 * Two-step verification card on the account page (MYN-241), the dark box in
 * the client-approved Option D layout (MYN-217): create a new set
 * of recovery codes when the old ones are lost. A confirm dialog first warns
 * that the old codes stop working; a failure stays inline in that dialog. The
 * new codes then show once, in a second dialog; closing it forgets them.
 * Below it, the optional authenticator re-scan (FR-220): after an email
 * change the app entry still shows the old email until it is re-scanned.
 */
export function TwoFactorCard() {
  const regenerate = useRegenerateRecoveryCodes();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rescanOpen, setRescanOpen] = useState(false);

  const close = () => {
    setConfirmOpen(false);
    regenerate.reset();
  };

  return (
    <section className="rounded-card bg-navy flex h-full flex-col gap-2.5 p-7 text-white">
      <h2 className="font-sans text-[1.0625rem] font-semibold">{S.SECTION_TITLE}</h2>
      <p className="text-sm font-medium">{S.FIELD_LABEL}</p>
      <p className="text-on-navy-muted text-sm leading-[1.3125rem]">{S.FIELD_HINT}</p>
      <Button variant="link-on-dark" onClick={() => setConfirmOpen(true)}>
        {S.REGENERATE}
      </Button>
      <p className="mt-auto text-sm font-medium">{S.RESCAN_LABEL}</p>
      <p className="text-on-navy-muted text-sm leading-[1.3125rem]">{S.RESCAN_HINT}</p>
      <Button variant="link-on-dark" onClick={() => setRescanOpen(true)}>
        {S.RESCAN}
      </Button>
      <RescanAuthenticatorDialog open={rescanOpen} onOpenChange={setRescanOpen} />
      <ConfirmDialog
        open={confirmOpen && !regenerate.isSuccess}
        onOpenChange={(open) => !open && close()}
        title={S.CONFIRM_TITLE}
        description={S.CONFIRM_DESCRIPTION}
        confirmLabel={regenerate.isPending ? S.REGENERATING : S.CONFIRM}
        confirmVariant="navy"
        onConfirm={() => regenerate.mutate()}
        isConfirming={regenerate.isPending}
      >
        {regenerate.isError && (
          <p role="alert" className="text-danger text-sm">
            {getApiErrorMessage(
              regenerate.error,
              mapApiError(regenerate.error, ERROR_BY_CODE, S.FAILED),
            )}
          </p>
        )}
      </ConfirmDialog>
      <Dialog
        open={regenerate.isSuccess}
        onOpenChange={(open) => !open && close()}
        title={S.DIALOG_TITLE}
        size="lg"
        footer={
          <Button variant="navy" className="px-6! py-2.5!" onClick={close}>
            {S.DONE}
          </Button>
        }
      >
        {regenerate.data && <RecoveryCodesList codes={regenerate.data} />}
      </Dialog>
    </section>
  );
}
